import { useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { ArrowDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { ConnectorIcon } from "../ConnectorIcon";
import {
    ConnectorType,
    StatementType,
} from "@/entities/DataAssociation/File.model";
import { TreeRootItem } from "./TreeRootItem";
import {
    FileTree,
    PriorityCategory,
    STATEMENT_TYPE_LABELS,
} from "../../Labeling.types";
import { connectorTreeListAtom } from "../../state/files.state";
import { labelingParamsAtom } from "../../state/params.state";
import {
    PRIORITY_CATEGORIES,
    STATEMENT_TYPES,
    FILE_CATEGORY_LABELS,
} from "../../labeling.constants";

type ConnectorTreeItemData = {
    sourceConnectorId: string;
    sourceConnectorType: string;
    hasUnlabeledFiles: boolean;
    mostRecent: Record<
        Exclude<StatementType, StatementType.MIXED>,
        FileTree | null
    >;
    historical: Record<
        Exclude<StatementType, StatementType.MIXED>,
        FileTree | null
    >;
    current: FileTree | null;
};

function ConnectorTreeItem({
    connector,
    isOpen,
    onToggle,
}: {
    connector: ConnectorTreeItemData;
    isOpen: boolean;
    onToggle: () => void;
}) {
    const groupedItems = useMemo(() => {
        const result: {
            category: PriorityCategory;
            label: string;
            files: {
                file: FileTree;
                statementType: string;
            }[];
        }[] = [];

        for (const category of PRIORITY_CATEGORIES) {
            const files: {
                file: FileTree;
                statementType: string;
            }[] = [];

            for (const statementType of STATEMENT_TYPES) {
                const file = connector[category][statementType];
                if (file) {
                    files.push({
                        file,
                        statementType: STATEMENT_TYPE_LABELS[statementType],
                    });
                }
            }

            if (files.length > 0) {
                result.push({
                    category,
                    label: FILE_CATEGORY_LABELS[category],
                    files,
                });
            }
        }

        return result;
    }, [connector]);

    return (
        <div>
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                    <ConnectorIcon
                        connectorType={
                            connector.sourceConnectorType as ConnectorType
                        }
                        large
                    />
                    <p
                        className={clsx(
                            "body-s cursor-pointer pt-0.5 text-slate-500",
                        )}
                    >
                        {connector.sourceConnectorId.slice(-6)}
                    </p>
                </div>
                <p className="body-micro text-slate-500">
                    {connector.hasUnlabeledFiles
                        ? "Has unlabeled files"
                        : "Labeled"}
                </p>
            </div>
            <span className={clsx("py-1", "flex items-center gap-3")}>
                <button
                    onClick={onToggle}
                    className={clsx(
                        "no-styles",
                        "text-button subdued flex items-center gap-1 font-light",
                        "hover:text-white",
                    )}
                >
                    {isOpen ? "Hide details" : "See details"}
                    <motion.span
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{
                            type: "spring",
                            bounce: 0,
                            duration: 0.4,
                        }}
                        className="flex"
                    >
                        <ArrowDownIcon className="size-3 text-gray-500" />
                    </motion.span>
                </button>
            </span>

            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: "auto" }}
                        exit={{ height: 0 }}
                        transition={{
                            type: "spring",
                            bounce: 0,
                            duration: 0.4,
                        }}
                        className="overflow-hidden"
                    >
                        <ul className={clsx("flex flex-col gap-7", " pt-3")}>
                            {connector.current && (
                                <li>
                                    <h2 className="heading-s mb-3 text-slate-500">
                                        {FILE_CATEGORY_LABELS.current}
                                    </h2>
                                    <ul className="flex flex-col gap-6">
                                        <TreeRootItem
                                            {...connector.current}
                                            name={
                                                connector.current.documentKind
                                                    ? STATEMENT_TYPE_LABELS[
                                                          connector.current
                                                              .documentKind
                                                      ]
                                                    : "Cleaned File"
                                            }
                                        />
                                    </ul>
                                </li>
                            )}
                            {groupedItems.map(({ category, label, files }) => (
                                <li key={category}>
                                    <h2 className="heading-s mb-3 text-slate-500">
                                        {label}
                                    </h2>
                                    <ul className="flex flex-col gap-6">
                                        {files.map(
                                            ({ file, statementType }) => (
                                                <TreeRootItem
                                                    key={file.id}
                                                    {...file}
                                                    name={statementType}
                                                />
                                            ),
                                        )}
                                    </ul>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function findConnectorWithFile(
    data: ConnectorTreeItemData[],
    fileId: string,
): string | null {
    for (const connector of data) {
        const allFiles = [
            connector.current,
            connector.mostRecent.income_statement,
            connector.mostRecent.balance_sheet,
            connector.historical.income_statement,
            connector.historical.balance_sheet,
        ].filter((f): f is FileTree => f !== null);

        for (const file of allFiles) {
            if (file.id === fileId) {
                return `${connector.sourceConnectorType}:${connector.sourceConnectorId}`;
            }
            if (file.labeledFiles?.some((lf) => lf.id === fileId)) {
                return `${connector.sourceConnectorType}:${connector.sourceConnectorId}`;
            }
        }
    }
    return null;
}

export function ConnectorTreeList() {
    const { data, isLoading } = useAtomValue(connectorTreeListAtom);
    const { selectedFileId } = useAtomValue(labelingParamsAtom);
    const [openConnectorId, setOpenConnectorId] = useState<string | null>(null);
    const hasInitialized = useRef(false);

    useEffect(() => {
        if (data && data.length > 0 && !hasInitialized.current) {
            hasInitialized.current = true;
            const found = selectedFileId
                ? findConnectorWithFile(data, selectedFileId)
                : null;
            const firstConnectorId = `${data[0].sourceConnectorType}:${data[0].sourceConnectorId}`;
            setOpenConnectorId(found ?? firstConnectorId);
        }
    }, [data, selectedFileId]);

    if (isLoading && !data) return <div>Loading...</div>;

    if (!data || data.length === 0) return null;

    return (
        <div className="flex flex-col gap-8">
            {data.map((connector) => {
                const connectorId = `${connector.sourceConnectorType}:${connector.sourceConnectorId}`;
                return (
                    <ConnectorTreeItem
                        key={connectorId}
                        connector={connector}
                        isOpen={openConnectorId === connectorId}
                        onToggle={() =>
                            setOpenConnectorId((prev) =>
                                prev === connectorId ? null : connectorId,
                            )
                        }
                    />
                );
            })}
        </div>
    );
}
